import {useFormContext} from '@yandex-delivery/form'
import {useDebounce} from '@yandex-delivery/hooks'
import {filter, pipe} from '@yandex-delivery/utils'
import {useEffect, useMemo} from 'react'

import {i18n} from 'src/shared/config'
import type {DeliveryInterval, Order} from 'src/shared/types'

import {useFetchNddDeliveryIntervals} from '../use-fetch-ndd-delivery-intervals'
import {useParcelPhysicalDimensions} from '../use-parcel-physical-dimensions'
import {
  isValidDeliveryInterval,
  mapDeliveryIntervals,
  withCorrectDeliveryDate,
  withCorrectDeliveryInterval,
} from '../utils'

const setFieldOptions = {
  shouldDirty: false,
  shouldTouch: false,
  shouldValidate: false,
}

export function useNddDeliveryIntervals() {
  const {isDeliveryIntervalsPending, deliveryIntervals, fetchDeliveryIntervalsHandler, error} =
    useFetchNddDeliveryIntervals()
  const {watch, clearErrors, setFieldValue, setFieldError} = useFormContext<Order>()
  const {places, totalSize} = useParcelPhysicalDimensions()

  // новый компонент для мобилы записывает айдишник по второму пути, старый для десктопа по первому.
  // Тут либо записывать дважды, либо проверять дважды, аксиома Эскобара
  const sourcePointId = watch('sourcePoint.dropOffPoint.point.id') || watch('ndd.route.source.pickupPoint._id')
  const destinationPointPickUpPointId =
    watch('destinationPoints.0.pickUpPoint.point.id') || watch('ndd.route.destination.pickupPoint._id')

  const {deliveryIntervalsMap, dateList, preparedDeliveryIntervals} = useMemo(() => {
    return pipe(
      deliveryIntervals || [], //
      filter(isValidDeliveryInterval),
      (intervals: DeliveryInterval[] = []) => {
        const intervalsMap = mapDeliveryIntervals(intervals)
        const intervalsDateList = Object.keys(intervalsMap)
        const preparedIntervals = intervals.map((deliveryInterval: DeliveryInterval) => ({
          from: deliveryInterval.from.toString(),
          to: deliveryInterval.to.toString(),
        }))

        return {
          deliveryIntervalsMap: intervalsMap,
          dateList: intervalsDateList,
          preparedDeliveryIntervals: preparedIntervals,
        } as const
      },
    )
  }, [deliveryIntervals])

  /**
   * @BusinessLogic
   * Если пришла ошибка при запросе интервалов доставки или интервалы доставки отсутствуют
   * необходимо установить ошибки в поля Откуда и Куда и обнулить значения интервалов доставки
   */
  useEffect(() => {
    if (error) {
      setFieldError(
        'destinationPoints.0.pickUpPoint.point',
        i18n('ndd.offers.info.error', 'К сожалению, доставка по указанному адресу недоступна'),
      )
      setFieldError(
        'sourcePoint.dropOffPoint.point',
        i18n('ndd.offers.info.error', 'К сожалению, доставка по указанному адресу недоступна'),
      )

      setFieldValue('ndd.__deliveryIntervalsMap', {}, setFieldOptions)
      setFieldValue('ndd.__deliveryIntervalsRaw', [], setFieldOptions)
      setFieldValue('ndd.deliveryDate', undefined, setFieldOptions)
      setFieldValue('ndd.deliveryInterval', undefined, setFieldOptions)

      return
    }

    clearErrors('sourcePoint.dropOffPoint.point')
    clearErrors('destinationPoints.0.pickUpPoint.point')
  }, [clearErrors, error, setFieldError, setFieldValue])

  /**
   * @BusinessLogic
   * При изменении адреса или пункта самовывоза, а также при изменении типа доставки
   * необходимо обновить список доступных интервалов доставки прежде обнулив предыдущие значения
   */
  useDebounce(
    () => {
      if (!destinationPointPickUpPointId || !sourcePointId || totalSize === 0) {
        return
      }

      fetchDeliveryIntervalsHandler({
        self_pickup_id: destinationPointPickUpPointId,
        station_id: sourcePointId,
        places,
      })

      setFieldValue('ndd.__deliveryIntervalsMap', deliveryIntervalsMap, setFieldOptions)
      setFieldValue('ndd.__deliveryIntervalsRaw', preparedDeliveryIntervals, setFieldOptions)
      setFieldValue('ndd.deliveryDate', withCorrectDeliveryDate(preparedDeliveryIntervals), setFieldOptions)
      setFieldValue('ndd.deliveryInterval', withCorrectDeliveryInterval(preparedDeliveryIntervals), setFieldOptions)
    },
    300,
    [sourcePointId, totalSize, destinationPointPickUpPointId],
  )

  return {
    isDeliveryIntervalsPending,
    dateList,
  }
}
